import { Color } from 'three';

/**
 * Height → color mapping for the point cloud.
 *
 * The ramp is a "turbo"-style perceptual sequence (dark blue → cyan → green →
 * yellow → red). It's monotonic in perceived brightness/hue so it reads as
 * "more height", and every stop is vivid against the dark background.
 */
const RAMP: Color[] = [
  '#30123b', '#4145ab', '#4675ed', '#39a2fc', '#1bcfd4', '#24eca6',
  '#61fc6c', '#a4fc3b', '#d1e834', '#f3c63a', '#fe9b2d', '#f36315',
  '#d93806', '#b11901',
].map((hex) => new Color(hex));

export interface HeightRange {
  zLow: number;
  zHigh: number;
}

/**
 * Robust Z range via percentile clamping (default p2–p98).
 *
 * PandaSet height is heavily bottom-weighted (median ~0.5m, a few structures
 * up to ~19m). Clamping to percentiles keeps the gradient's range where the
 * points actually are, instead of wasting it on a handful of tall outliers.
 */
export function heightRange(
  positions: Float32Array,
  lowPct = 0.02,
  highPct = 0.98,
): HeightRange {
  const n = positions.length / 3;
  const zs = new Float32Array(n);
  for (let i = 0; i < n; i++) zs[i] = positions[i * 3 + 2];
  zs.sort();
  const zLow = zs[Math.floor((n - 1) * lowPct)];
  const zHigh = zs[Math.floor((n - 1) * highPct)];
  return { zLow, zHigh: zHigh <= zLow ? zLow + 1e-6 : zHigh };
}

/** Sample the ramp at t ∈ [0,1], lerping between adjacent stops. */
function sampleRamp(t: number, out: Color): Color {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  const scaled = clamped * (RAMP.length - 1);
  const i = Math.floor(scaled);
  const next = Math.min(RAMP.length - 1, i + 1);
  return out.copy(RAMP[i]).lerp(RAMP[next], scaled - i);
}

/**
 * Build a packed RGB Float32Array (values 0..1) mapping each point's Z through
 * the ramp. Matches the layout of `positions` so it drops straight into a
 * `color` buffer attribute with `vertexColors`.
 */
export function buildHeightColors(positions: Float32Array, range: HeightRange): Float32Array {
  const n = positions.length / 3;
  const colors = new Float32Array(n * 3);
  const span = range.zHigh - range.zLow;
  const c = new Color();
  for (let i = 0; i < n; i++) {
    const t = (positions[i * 3 + 2] - range.zLow) / span;
    sampleRamp(t, c);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  return colors;
}
